import { Filial } from "app/components/movimentacao-estoque/MovimentacaoEstoque";
import { ILotesReservados } from "app/types/ILotesReservados";
import globalMU from "globalMU";
import React, { useMemo } from "react";
import { Paginacao } from "../paginacao/Paginacao";

const estiloLotesReservados = require("./lotes-reservados.module.scss").default;
const estiloTabela = require('../../../estilos/tabelas.module.scss').default

export type LotesReservadosProps = {
    dados: ILotesReservados;
    filiais: Filial[];
    produto: string;
    descricao: string;
    permiteVisualizarValidade: boolean;
    /**
     * Filial escolhida no combo. Vem do container (e não de state local) porque a paginação também
     * precisa dela: sem isso, virar de página descartava o filtro de filial (ver comentário em
     * LotesReservadosContainer.onPesquisarPaginado).
     */
    filialSelecionada: string;
    onPesquisarPorFilial: (numeroPagina: number, filial: string) => void;
    onPesquisarPaginado: (numeroPagina: number) => void
};

const LotesReservadosComponent = (props: LotesReservadosProps) => {

    const detalheProdutoSelecionado = useMemo(() => (
        <div id="LotesReservados_DetalheProduto" className={estiloLotesReservados.form}>
            <label id="LotesReservados_ProdutoLabel">Produto</label>
            <div id="LotesReservados_Produto">{props.produto}</div>

            <label id="LotesReservados_DescricaoLabel">Descrição</label>
            <div id="LotesReservados_Descricao">{props.descricao}</div>

            <label id="LotesReservados_FilialLabel">Filial</label>
            <select
                id="LotesReservados_SelectFilial"
                className="_w[200px]"
                value={props.filialSelecionada ?? ""}
                onChange={(e) => props.onPesquisarPorFilial(null, e.target.value)}
            >
                <option value="">Todas</option>
                {props.filiais?.map((filial) => (
                    <option key={filial.Codigo} value={filial.Codigo}>
                        {filial.Descricao}
                    </option>
                ))}
            </select>
        </div>
    ), [props.produto, props.descricao, props.filiais, props.filialSelecionada, props.onPesquisarPorFilial])

    const cabecalhoTabelaLoteReservado = useMemo(() => (
        <thead id="LotesReservados_Cabecalho" style={{ textAlign: "center" }} className={estiloLotesReservados.tabela}>
            <tr>
                <th colSpan={3}>Filial</th>
                <th rowSpan={2} style={{ verticalAlign: "bottom" }} className={estiloTabela.cabecalhoTexto}>Vendedor</th>
                <th rowSpan={2} style={{ verticalAlign: "bottom" }} className={estiloTabela.cabecalhoTexto}>Pedido</th>
                <th rowSpan={2} style={{ verticalAlign: "bottom" }} className={estiloTabela.cabecalhoTexto}>Lote</th>
                <th colSpan={2} className={estiloLotesReservados.textCentro}>Saída Lote</th>
                <th colSpan={2} className={estiloLotesReservados.textCentro}>Reservado</th>
                <th rowSpan={2} style={{ verticalAlign: "bottom" }} className={estiloTabela.cabecalhoTexto}>Situação</th>
                {props.permiteVisualizarValidade &&
                    <th rowSpan={2} style={{ verticalAlign: "bottom" }} className={estiloLotesReservados.textoDireita}>Validade</th>
                }
            </tr>
            <tr className={estiloTabela.cabecalhoNumerico}>
                <th className={estiloTabela.cabecalhoTexto}>Venda</th>
                <th className={estiloTabela.cabecalhoTexto}>Retirada</th>
                <th className={estiloTabela.cabecalhoTexto}>Transf.</th>
                <th>Quant.</th>
                <th>Caixas</th>
                <th>Quant.</th>
                <th>Caixas</th>
            </tr>
        </thead>
    ), [props.permiteVisualizarValidade])

    const corpoTabelaLotesReservados = useMemo(() => (
        <tbody id="LotesReservados_Corpo">
            {props.dados?.Lotes?.Entities?.map((dado, indice) => {
                // O contrato (DadosEntitiesLotesReservados) não tem campo de identidade, então a key
                // é composta pelos campos que identificam a reserva + o índice como desempate. É
                // aceitável aqui porque a lista é somente-leitura e recarregada inteira a cada
                // página/filtro — nunca reordenada nem editada no cliente.
                const chave = `${dado?.Pedido}-${dado?.Lote}-${dado?.Filial}-${dado?.FilialRetirada}-${indice}`

                return (
                    <tr
                        key={chave}
                        id={`LotesReservados_Linha_${indice}`}
                        className={`_table-row ${estiloTabela.dadoNumerico} ${estiloTabela.linhaClicavel}`}
                    >
                        <td>{dado?.Filial}</td>
                        <td className={estiloTabela.dadoTexto}>{dado?.FilialRetirada}</td>
                        <td className={estiloTabela.dadoTexto}>{dado?.FilialTransferencia}</td>
                        <td className={estiloTabela.dadoTexto}>{dado?.Vendedor}</td>
                        <td className={estiloTabela.dadoTexto}>{dado?.Pedido}</td>
                        <td className={estiloTabela.dadoTexto}>{dado?.Lote}</td>
                        <td>{globalMU.formatarValorNumerico(dado?.Quantidade, 3)}</td>
                        <td>{globalMU.formatarValorNumerico(dado?.Caixas, 0)}</td>
                        <td>{globalMU.formatarValorNumerico(dado?.Reservado, 3)}</td>
                        <td>{globalMU.formatarValorNumerico(dado?.CaixasReservado, 0)}</td>
                        <td className={estiloTabela.dadoTexto}>{dado?.DescricaoSituacao}</td>
                        {props.permiteVisualizarValidade && (
                            <td style={{ color: dado?.LoteVencido ? "red" : "black" }}>
                                {globalMU.formatDate(dado?.Validade)}
                            </td>
                        )}
                    </tr>
                )
            })}
        </tbody>
    ), [props.dados?.Lotes?.Entities, props.permiteVisualizarValidade])

    const rodapeLoteReservado = useMemo(() => (
        <tfoot id="LotesReservados_Rodape">
            <tr id="LotesReservados_TotalPagina">
                <th colSpan={5} className={estiloTabela.cabecalhoNumerico}>Total</th>
                <th className={estiloTabela.cabecalhoNumerico}>{globalMU.formatarValorNumerico(props.dados?.QuantidadeLotesPagina, 0)}</th>
                <th className={estiloTabela.cabecalhoNumerico}>{globalMU.formatarValorNumerico(props.dados?.QuantidadeSaidaLotePagina, 3)}</th>
                <th className={estiloTabela.cabecalhoNumerico}>{globalMU.formatarValorNumerico(props.dados?.QuantidadeCaixasSaidaLotePagina, 0)}</th>
                <th className={estiloTabela.cabecalhoNumerico}>{globalMU.formatarValorNumerico(props.dados?.QuantidadeReservaLotePagina, 3)}</th>
                <th className={estiloTabela.cabecalhoNumerico}>{globalMU.formatarValorNumerico(props.dados?.QuantidadeCaixasReservaLotePagina, 0)}</th>
                <th></th>
                {props.permiteVisualizarValidade && (<th></th>)}
            </tr>
            <tr id="LotesReservados_TotalGeral">
                <th className={estiloTabela.cabecalhoNumerico} colSpan={5}>Total Geral</th>
                <th className={estiloTabela.cabecalhoNumerico}>{globalMU.formatarValorNumerico(props.dados?.Totais?.QuantidadeLotes, 0)}</th>
                <th className={estiloTabela.cabecalhoNumerico}>{globalMU.formatarValorNumerico(props.dados?.Totais?.QuantidadeSaidaLote, 3)}</th>
                <th className={estiloTabela.cabecalhoNumerico}>{globalMU.formatarValorNumerico(props.dados?.Totais?.QuantidadeCaixasSaidaLote, 0)}</th>
                <th className={estiloTabela.cabecalhoNumerico}>{globalMU.formatarValorNumerico(props.dados?.Totais?.QuantidadeReservaLote, 3)}</th>
                <th className={estiloTabela.cabecalhoNumerico}>{globalMU.formatarValorNumerico(props.dados?.Totais?.QuantidadeCaixasReservaLote, 0)}</th>
                <th></th>
                {props.permiteVisualizarValidade && (<th></th>)}
            </tr>
        </tfoot>
    ), [props.dados, props.permiteVisualizarValidade])

    const paginacao = props.dados?.Lotes?.PageCount > 1 && (
        <Paginacao
            paginaAtual={props.dados?.Lotes?.ActualPage}
            totalPaginas={props.dados?.Lotes?.PageCount}
            id="LotesReservados_Paginacao"
            onAlterarListaSelecionada={props.onPesquisarPaginado}
        />
    )

    return (
        <>
            <div id="LotesReservados_DivDetalheProduto" style={{ overflowX: "auto" }}>
                {detalheProdutoSelecionado}
            </div>

            <div id="LotesReservados_TabelaContainer" className={estiloTabela.containerRolavel}>
                <table id="LotesReservados_Tabela" className={`_table _w-full _border-collapse text-right ${estiloTabela.tabela}`}>
                    {cabecalhoTabelaLoteReservado}
                    {corpoTabelaLotesReservados}
                    {rodapeLoteReservado}
                </table>
            </div>

            {paginacao}

            <section id="LotesReservados_EspacamentoFinal" style={{ marginBottom: "100px" }} />
        </>
    )
}

export const LotesReservados = React.memo(LotesReservadosComponent)
LotesReservados.displayName = "LotesReservados"
